import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '@finn-auto/design-system/atoms/modal';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { fetchTicketSlackContact } from '../../services/bpTicketsApi';
import type { BpTicket, TicketCategory, TicketPriority, TicketStatus } from '../../types/bpTickets';
import { CategoryBadge } from './badges';
import { nameFromEmail } from './format';
import { StatusSelect } from './StatusSelect';
import { CategorySelect } from './CategorySelect';
import { PrioritySelect } from './PrioritySelect';
import { AssigneeSelect, type AssigneeOption } from './AssigneeSelect';
import { buildTicketOutreachMessage, slackDmUrl } from './slackOutreach';

export function TicketDetailModal({
  ticket,
  assigneeOptions,
  onClose,
  onStatusChange,
  onCategoryChange,
  onPriorityChange,
  onAssigneeChange,
}: {
  ticket: BpTicket;
  assigneeOptions: AssigneeOption[];
  onClose: () => void;
  onStatusChange: (status: TicketStatus) => void;
  onCategoryChange: (category: TicketCategory) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  onAssigneeChange: (assignee: AssigneeOption) => void;
}) {
  const [slackLoading, setSlackLoading] = useState(false);

  async function handleMessageOnSlack() {
    setSlackLoading(true);
    // Declared here, not inside the try block, so the catch below can still close it if opening
    // succeeded but a later step (the lookup, the clipboard write) throws.
    let slackTab: Window | null = null;
    try {
      // Written to the clipboard BEFORE opening any tab: window.open() below typically moves
      // focus to the new tab, and the Clipboard API throws "Document is not focused" once that
      // happens — so this order has to run while the click that started this handler still owns
      // focus, not after.
      await navigator.clipboard.writeText(buildTicketOutreachMessage(ticket));
      // Opened synchronously, right after that — not after the network lookup below resolves.
      // Safari (and, past a few seconds, Chrome/Firefox too) only treats window.open() as
      // user-initiated within roughly that same tick; once there's a real network round-trip in
      // between, it silently drops the popup. That silent drop was the "Message on Slack doesn't
      // work" report — the toast still said a DM "just opened" because window.open() returning
      // null throws nothing. Navigated once the lookup resolves, below.
      slackTab = window.open('', '_blank');
      const { slackUserId } = await fetchTicketSlackContact(ticket.id);
      if (slackTab) {
        slackTab.location.href = slackDmUrl(slackUserId);
        // Sever the back-reference now that we're navigating the tab ourselves, rather than
        // passing 'noopener' to window.open() up front — 'noopener' makes window.open() itself
        // return null, which would leave nothing here to navigate.
        slackTab.opener = null;
        showToast({ status: 'success', description: 'Drafted message copied — paste it into the Slack DM that just opened.' });
      } else {
        showToast({
          status: 'info',
          description: "Drafted message copied, but your browser blocked the new tab — open Slack yourself and paste it in.",
        });
      }
    } catch (err) {
      slackTab?.close();
      showToast({ status: 'error', description: err instanceof Error ? err.message : 'Could not open Slack' });
    } finally {
      setSlackLoading(false);
    }
  }

  return (
    <Modal open onOpenChange={(next: boolean) => !next && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-4rem)] !w-[min(100vw-2rem,32rem)] gap-2 overflow-auto rounded p-0">
        <ModalHeader className="border-b border-pearl px-6 py-5">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-iron">#{ticket.ticketNumber}</span>
              <StatusSelect status={ticket.status} onChange={onStatusChange} />
            </div>
            {/* ModalTitle defaults to a 28-40px web/mobile title style (mobile-t4-semibold /
                web-t4-semibold) meant for page headers, not a ~512px-wide modal — it dominated
                the dialog and forced long summaries onto many lines. Those come from a Tailwind
                plugin utility that is emitted after core utilities, so a plain `text-lg` class
                loses the cascade; `!` forces it, the same technique already used on
                ModalContent's size overrides below. On its own row (rather than sharing one with
                the status tile) it gets the full header width instead of being squeezed into
                whatever the status tile left over. */}
            <ModalTitle className="!mt-0 !text-lg !font-semibold !leading-snug !text-black">
              {ticket.summary}
            </ModalTitle>
          </div>
        </ModalHeader>

        <div className="space-y-5 px-6 py-5">
          <p className="whitespace-pre-wrap rounded bg-cotton px-4 py-3 text-sm text-black">{ticket.details}</p>

          <div className="flex flex-wrap gap-2">
            <PrioritySelect priority={ticket.priority} onChange={onPriorityChange} />
            {ticket.category ? (
              <CategorySelect category={ticket.category} onChange={onCategoryChange} />
            ) : (
              // CategorySelect always needs a real value to write back — a ticket with no category
              // on file yet shows the plain (null-safe) badge until one is set some other way,
              // rather than offering a select whose "change" would have no current value to diff
              // against.
              <CategoryBadge category={ticket.category} />
            )}
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <div>
              <dt className="text-xs text-iron">Submitted by</dt>
              <dd className="mt-0.5 font-medium text-black">{nameFromEmail(ticket.submittedBy)}</dd>
            </div>
            <div>
              <dt className="text-xs text-iron">Business Partner</dt>
              <dd className="mt-0.5">
                <AssigneeSelect dri={ticket.dri} options={assigneeOptions} onChange={onAssigneeChange} />
              </dd>
            </div>
            <div>
              <dt className="text-xs text-iron">Department</dt>
              <dd className="mt-0.5 font-medium text-black">{ticket.department || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-iron">Created</dt>
              <dd className="mt-0.5 font-medium text-black">
                {ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : '—'}
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-pearl px-6 py-4">
          {ticket.slackSource ? (
            <a
              href={ticket.slackSource}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-trustedBlue hover:underline"
            >
              View original Slack message →
            </a>
          ) : (
            <span />
          )}

          {/* gap goes on contentClassName, not className: the Button atom wraps children in its
              own inner "content" flex div (see button.js) — icon and text are direct children of
              that div, not of the outer <button>, so a gap on the outer element has no effect
              between them. */}
          <Button
            variant="outline"
            size="sm"
            className="w-fit shrink-0 rounded"
            contentClassName="gap-3"
            onClick={handleMessageOnSlack}
            disabled={slackLoading}
          >
            <MessageSquare className="h-4 w-4" />
            {slackLoading ? 'Looking up on Slack…' : 'Message on Slack'}
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}
